/* eslint-disable @typescript-eslint/camelcase */
import {
  FormControlLabel,
  Grid,
  makeStyles,
  Switch,
  Tooltip,
} from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import Button from 'components/Button';
import Loading from 'components/Loading';
import Permission from 'components/ValidPermission';
import { Formik } from 'formik';
import i18n from 'i18n';
import {
  EnvironmentFields,
  FormSupportServerProps,
} from 'modules/environments/interfaces';
import React from 'react';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { applyI18n } from 'utils/helpers';
import formStyles from '../../../Form/styles';
import screenFields from './formFields';
import validationSchema from './schema';

const useStyles = makeStyles(formStyles);

const initial: FormSupportServerProps = {
  instance_id: '',
  include_instance_as_application_server: false,
  monitoring_management_enabled: true,
};

interface SwitchComponentProps {
  children?: React.ReactNode;
  hidden?: boolean;
  id?: string;
  isChecked: boolean;
  label: string;
  onChange: (e: any, value: any) => void;
}

export interface Props {
  environment: EnvironmentFields | null;
  initialValues?: FormSupportServerProps;
  isLoading: boolean;
  onCancel: () => void;
  onSubmit: (values: FormSupportServerProps) => void;
}

const SwitchComponent = ({
  children,
  hidden,
  id,
  isChecked,
  label,
  onChange,
}: SwitchComponentProps) => {
  const classes = useStyles();

  return hidden ? null : (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          classes={{ label: classes.switchLabel }}
          control={
            <Switch
              id={id}
              checked={isChecked}
              onChange={onChange}
              classes={{ colorSecondary: classes.switchChecked }}
              size="small"
            />
          }
          label={label}
        />
      </Grid>
      {!!children && isChecked && children}
    </Grid>
  );
};

export default function FormSupportServer({
  environment,
  initialValues = initial,
  isLoading,
  onCancel,
  onSubmit,
}: Props) {
  const classes = useStyles();

  return (
    <Grid item container data-testid={'autosky-formsupportserver'}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ setFieldValue, submitForm, errors, values }) => (
          <Grid container spacing={2}>
            <GenericForm
              fields={values}
              screenFields={applyI18n(screenFields, i18n.language)}
              setFieldValue={setFieldValue}
              errors={errors}
            />
            {environment?.behavior.type === 'micro' && (
              <Grid item xs={12}>
                <SwitchComponent
                  id="toggle-add-new-aplication-instance"
                  label={i18n.t(
                    'ENVIRONMENTS_PAGE.INCLUDE_INSTANCE_LIKE_SERVER_APPLICATION'
                  )}
                  onChange={(e, value) =>
                    setFieldValue(
                      'include_instance_as_application_server',
                      !!value
                    )
                  }
                  isChecked={values.include_instance_as_application_server}
                />
              </Grid>
            )}

            <Permission value="can_enable_server_monitoring_management">
              <Grid item container xs={12} alignItems="center">
                <Grid item>
                  <SwitchComponent
                    id="enable-server-monitoring-management"
                    label={i18n.t('CAN_ENABLE_SERVER_MONITORING_MANAGEMENT')}
                    onChange={(e, value) =>
                      setFieldValue('monitoring_management_enabled', !!value)
                    }
                    isChecked={values.monitoring_management_enabled}
                  />
                </Grid>
                <Tooltip
                  title={i18n.t(
                    'ENABLE_SERVER_MONITORING_MANAGEMENT_INFO_TEXT_KEY'
                  )}
                >
                  <InfoOutlined className={classes.infoIcon} />
                </Tooltip>
              </Grid>
            </Permission>

            <Grid container justify="flex-end" className={classes.buttonsBase}>
              <Grid container className={classes.buttonsAlign}>
                <Button
                  id="submit-button"
                  onClick={submitForm}
                  submitButton
                  disabled={isLoading}
                >
                  {isLoading ? <Loading size={15} /> : i18n.t('ADD_KEY')}
                </Button>
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={onCancel}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Formik>
    </Grid>
  );
}
